import React, { useEffect, useMemo, useRef } from "react"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList } from "@ui/shad"

import { MentionItem } from "./mention-item"
import { MentionOption } from "./types"

interface MentionDropdownProps {
  options: MentionOption[]
  selectedIndex: number
  searching: boolean
  searchQuery: string
  onSelect: (option: MentionOption) => void
  onOpenChange?: (open: boolean) => void
  onSearchChange?: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  searchInputRef?: React.RefObject<HTMLInputElement | null>
}

function formatHeader(str: string): string {
  switch (str) {
    case "Person":
      return "People"
    case "Business":
      return "Businesses"
    case "CustodianAccount":
      return "Custodian accounts"
    case "FinancialAccount":
      return "Accounts"
    case "InsurancePolicy":
      return "Insurance policies"
    default:
      return `${str.charAt(0).toUpperCase()}${str.slice(1)}s`
  }
}

export function MentionDropdown({
  options,
  selectedIndex,
  searching,
  searchQuery,
  onSelect,
  onOpenChange,
  onSearchChange,
  onKeyDown,
  searchInputRef,
}: MentionDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    onOpenChange?.(true)
    return () => onOpenChange?.(false)
  }, [onOpenChange])

  const { groupedOptions, sortedTypes } = useMemo(() => {
    const groups: Record<string, MentionOption[]> = {}
    const types: string[] = []

    options.forEach((option) => {
      const type = option.recordType
      if (!groups[type]) {
        groups[type] = []
        types.push(type)
      }
      groups[type].push(option)
    })

    return { groupedOptions: groups, sortedTypes: types }
  }, [options])

  const showSearchInput = onSearchChange !== undefined

  return (
    <div
      ref={containerRef}
      className={
        showSearchInput
          ? "bg-background pointer-events-auto w-full overflow-hidden rounded-md shadow-none"
          : "bg-background pointer-events-auto fixed isolate z-9999 w-64 max-w-[calc(100vw-1rem)] overflow-hidden rounded-md border shadow-md backdrop-blur-none backdrop-filter-none"
      }
      style={showSearchInput ? undefined : { backgroundColor: "white" }}
      onClick={(e) => e.stopPropagation()}
      onWheel={(e) => {
        e.stopPropagation()
      }}
    >
      <Command
        className={
          showSearchInput ? "bg-background rounded-none border-0 shadow-none" : "bg-background rounded-lg shadow-none"
        }
        shouldFilter={!showSearchInput}
      >
        {showSearchInput && (
          <CommandInput
            ref={searchInputRef}
            value={searchQuery}
            onValueChange={onSearchChange}
            onKeyDown={onKeyDown}
            placeholder="Search for people, households..."
            className="border-0 focus:ring-0"
          />
        )}
        <CommandList className="bg-background max-h-[var(--mention-menu-max-height,300px)]">
          {options.length === 0 ? (
            <CommandEmpty>
              {searching ? "Searching..." : searchQuery ? "No results found" : "Start typing to search..."}
            </CommandEmpty>
          ) : (
            <>
              {(() => {
                let visualIndex = 0
                return sortedTypes.map((type) => (
                  <CommandGroup key={type} heading={formatHeader(type)} className="bg-background">
                    {groupedOptions[type].map((option) => {
                      const currentIndex = visualIndex++
                      return (
                        <MentionItem
                          key={option.recordId}
                          option={option}
                          isSelected={currentIndex === selectedIndex}
                          onClick={() => onSelect(option)}
                        />
                      )
                    })}
                  </CommandGroup>
                ))
              })()}
            </>
          )}
        </CommandList>
      </Command>
    </div>
  )
}
